import type { RunwayEnd } from './types';

export const R_EARTH = 6371000;
export const NM_M = 1852;
export const FT_M = 0.3048;
export const KT_MS = 0.514444;

export const toRad = (d: number) => (d * Math.PI) / 180;
export const toDeg = (r: number) => (r * 180) / Math.PI;
export const norm360 = (d: number) => ((d % 360) + 360) % 360;
export const norm180 = (d: number) => { const x = norm360(d); return x > 180 ? x - 360 : x; };
/** unsigned smallest difference between two bearings, 0..180 */
export const angleDiff = (a: number, b: number) => Math.abs(norm180(a - b));

export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(a));
}

export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1), φ2 = toRad(lat2), dλ = toRad(lon2 - lon1);
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return norm360(toDeg(Math.atan2(y, x)));
}

export function destination(lat: number, lon: number, bearing: number, distM: number): { lat: number; lon: number } {
  const δ = distM / R_EARTH, θ = toRad(bearing), φ1 = toRad(lat), λ1 = toRad(lon);
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: toDeg(φ2), lon: norm180(toDeg(λ2)) };
}

/** Local east/north metres from an origin (equirectangular; fine within ~50 km). */
export function enu(oLat: number, oLon: number, lat: number, lon: number): { x: number; y: number } {
  const x = toRad(lon - oLon) * R_EARTH * Math.cos(toRad(oLat));
  const y = toRad(lat - oLat) * R_EARTH;
  return { x, y };
}

export function fromEnu(oLat: number, oLon: number, x: number, y: number): { lat: number; lon: number } {
  return { lat: oLat + toDeg(y / R_EARTH), lon: oLon + toDeg(x / (R_EARTH * Math.cos(toRad(oLat)))) };
}

/** along = metres from threshold in runway direction; cross = metres right of centreline. */
export function runwayFrame(rw: RunwayEnd, lat: number, lon: number): { along: number; cross: number } {
  const { x, y } = enu(rw.lat, rw.lon, lat, lon);
  const θ = toRad(rw.headingTrue);
  return { along: x * Math.sin(θ) + y * Math.cos(θ), cross: x * Math.cos(θ) - y * Math.sin(θ) };
}

export function fromRunwayFrame(rw: RunwayEnd, along: number, cross: number): { lat: number; lon: number } {
  const θ = toRad(rw.headingTrue);
  const x = along * Math.sin(θ) + cross * Math.cos(θ);
  const y = along * Math.cos(θ) - cross * Math.sin(θ);
  return fromEnu(rw.lat, rw.lon, x, y);
}

/** Wind vector (east, north) in m/s for wind FROM `dirDeg` at `kt`. */
export function windVector(dirDeg: number, kt: number): { x: number; y: number } {
  const to = toRad(dirDeg + 180);
  return { x: kt * KT_MS * Math.sin(to), y: kt * KT_MS * Math.cos(to) };
}

export function mean(xs: number[]): number { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN; }
export function std(xs: number[]): number {
  if (xs.length < 2) return 0; const m = mean(xs);
  return Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / (xs.length - 1));
}
export function rms(xs: number[]): number { return xs.length ? Math.sqrt(mean(xs.map(x => x * x))) : NaN; }
export function median(xs: number[]): number {
  if (!xs.length) return NaN; const s = [...xs].sort((a, b) => a - b); const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}
/** ray-casting point-in-polygon; polygon as [lat, lon][] */
export function pointInPolygon(lat: number, lon: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i]!, [yj, xj] = poly[j]!;
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
